package com.tregz.tremenz.ui.label

/**
 * Platform neutral handle on a localized string. Each target resolves a key through [labelText];
 * common code never touches `R.string`.
 */
enum class LabelKey {
    ABOUT_LISTEN_BODY,
    ABOUT_LISTEN_TITLE,
    ABOUT_MORE,
    ABOUT_STYLES_BODY,
    ABOUT_STYLES_TITLE,
    ABOUT_WHAT_BODY,
    ABOUT_WHAT_TITLE,
    ABOUT_WHO_BODY,
    ABOUT_WHO_TITLE,
    ACTION_AGREE,
    ACTION_CANCEL,
    ACTION_CLOSE,
    ACTION_COPY,
    ACTION_RETRY,
    ACTION_SHARE,
    CHANNEL_APERO,
    CHANNEL_BISTRO,
    CHANNEL_CLUB,
    CHANNEL_OFF_AIR,
    CHANNEL_ON_AIR_SUFFIX,
    CHANNEL_OTHER,
    CHANNEL_TUNING_IN,
    CONTACT_EMAIL_LABEL,
    CONTACT_MAILTO_NOTE,
    CONTACT_MESSAGE_LABEL,
    CONTACT_NAME_LABEL,
    CONTACT_SUBMIT,
    CONTROL_AUDIO_ONLY,
    CONTROL_CONTINUOUS_MIX,
    CONTROL_START_FROM_BEGINNING,
    EMPTY_NEWS,
    EMPTY_PHOTOS,
    HEADING_ABOUT_SUBTITLE,
    HEADING_CONTACT_SUBTITLE,
    HEADING_NEWS_SUBTITLE,
    HEADING_PHOTOS_SUBTITLE,
    HERO_PHOTO,
    INFO_HIDE,
    INFO_SHOW,
    MEDIA_COLLAPSE,
    MEDIA_DETAILS,
    MEDIA_EXPAND,
    MEDIA_FEATURING,
    MEDIA_GENRE,
    MEDIA_MIXED_BY,
    MEDIA_NO_TRACKLIST,
    MEDIA_OPEN_ON,
    MEDIA_TRACKLIST,
    NEWS_2016_BODY,
    NEWS_2016_TITLE,
    NEWS_2017_BODY,
    NEWS_2017_TITLE,
    NEWS_2021_BODY,
    NEWS_2021_TITLE,
    NEWS_2026_BODY,
    NEWS_2026_TITLE,
    OUTPUT_TITLE,
    PHOTO_LOGO,
    PHOTO_VENUE_1_SUBTITLE,
    PHOTO_VENUE_2_SUBTITLE,
    PHOTO_VENUE_EVENTS,
    PLAYER_PAUSE,
    PLAYER_PLAY,
    PLAYER_UNAVAILABLE,
    ROUTE_ABOUT,
    ROUTE_CHANNELS,
    ROUTE_CONTACT,
    ROUTE_EDITS,
    ROUTE_MIXES,
    ROUTE_NEWS,
    ROUTE_PHOTOS,
    ROUTE_TRACKS,
    SHARE_COPIED,
    SHARE_MESSAGE,
    SHARE_TITLE,
    STATUS_EMPTY,
    STATUS_FAILED,
    STATUS_LOADING,
    TERMS_MESSAGE,
    TERMS_TITLE,
    THEME_DAY,
    THEME_NIGHT;

    companion object {
        const val VERSION = 5

        val defaults: Map<String, String> = mapOf(
            ABOUT_LISTEN_BODY.name to "Open the Tremenz web app to stream any live set for free, with a full track list for each mix. Recordings are also hosted on Mixcloud under the Trmnz and tremenz profiles.",
            ABOUT_LISTEN_TITLE.name to "How do I listen to the mixes?",
            ABOUT_MORE.name to "Read more on the web",
            ABOUT_STYLES_BODY.name to "Tremenz plays all-time hits across funk and disco grooves, electro swing, reggae, chill and lounge, house, tropical and moombahton, plus mashups and alternative rock. Each set is a continuous mix, so one style flows into the next without a break.",
            ABOUT_STYLES_TITLE.name to "Which musical styles does Tremenz play?",
            ABOUT_WHAT_BODY.name to "Tremenz is the free-streaming home of DJ/VJ Tremenz, the stage name of Jerome Robbins. Each live set is a continuous mix of about twenty tracks, blended without gaps so the music never stops.",
            ABOUT_WHAT_TITLE.name to "What is Tremenz?",
            ABOUT_WHO_BODY.name to "DJ/VJ Tremenz is Jerome Robbins, a DJ and VJ who has published mixtapes since 2014. He also builds the Tremenz apps for the web, Android and iOS, so the same mixes are available on every device.",
            ABOUT_WHO_TITLE.name to "Who is DJ/VJ Tremenz?",
            ACTION_AGREE.name to "Agree",
            ACTION_CANCEL.name to "Cancel",
            ACTION_CLOSE.name to "Close",
            ACTION_COPY.name to "Copy link",
            ACTION_RETRY.name to "Retry",
            ACTION_SHARE.name to "Share",
            CHANNEL_APERO.name to "Apero",
            CHANNEL_BISTRO.name to "Bistro",
            CHANNEL_CLUB.name to "Club",
            CHANNEL_OFF_AIR.name to "Nothing scheduled this hour",
            CHANNEL_ON_AIR_SUFFIX.name to "on air this hour",
            CHANNEL_OTHER.name to "Other",
            CHANNEL_TUNING_IN.name to "Tuning in…",
            CONTACT_EMAIL_LABEL.name to "Your email",
            CONTACT_MAILTO_NOTE.name to "This opens your mail app addressed to %s.",
            CONTACT_MESSAGE_LABEL.name to "Date, venue and anything else",
            CONTACT_NAME_LABEL.name to "Your name",
            CONTACT_SUBMIT.name to "Send booking request",
            CONTROL_AUDIO_ONLY.name to "Audio only",
            CONTROL_CONTINUOUS_MIX.name to "Continuous mix",
            CONTROL_START_FROM_BEGINNING.name to "Start from the beginning",
            EMPTY_NEWS.name to "No announcements yet. Check back soon.",
            EMPTY_PHOTOS.name to "The gallery is being put together. Check back soon.",
            HEADING_ABOUT_SUBTITLE.name to "Free continuous DJ mixes and music videos",
            HEADING_CONTACT_SUBTITLE.name to "Book Tremenz for your venue, your party or your event.",
            HEADING_NEWS_SUBTITLE.name to "Releases, residencies and bookings.",
            HEADING_PHOTOS_SUBTITLE.name to "Behind the decks, on the road and in the room.",
            HERO_PHOTO.name to "Photo of Jerome Tremenz",
            INFO_HIDE.name to "Hide information",
            INFO_SHOW.name to "Show information",
            MEDIA_COLLAPSE.name to "Collapse",
            MEDIA_DETAILS.name to "Details",
            MEDIA_EXPAND.name to "Expand",
            MEDIA_FEATURING.name to "Featuring",
            MEDIA_GENRE.name to "Genre",
            MEDIA_MIXED_BY.name to "Mixed by",
            MEDIA_NO_TRACKLIST.name to "No tracklist available",
            MEDIA_OPEN_ON.name to "Open on",
            MEDIA_TRACKLIST.name to "Tracklist",
            NEWS_2016_BODY.name to "Thanks to Vocalteknix for selecting some of my video edits for his weekly video mix broadcasted on Trace Urban.",
            NEWS_2016_TITLE.name to "As seen on Trace TV",
            NEWS_2017_BODY.name to "I'm looking forward to read your feedback for this faster and more user-friendly navigation. There is still so much more to be added. If you liked the facebook fanpage, you should get notifications for updates.\n\nPlease let me know if you notice any bug or error. Your support is tremendously appreciated.",
            NEWS_2017_TITLE.name to "New website design",
            NEWS_2021_BODY.name to "Video mixtapes are now available on Android. With the app you may stream to your tv with Chromecast. If you liked the facebook fanpage, you should get notifications for updates.\n\nPlease let me know if you notice any bug or error. Your support is tremendously appreciated.",
            NEWS_2021_TITLE.name to "New Android app",
            NEWS_2026_BODY.name to "A brand-new design for 2026, completely rebuilt with modern multiplatform technology. Enjoy a smoother, responsive layout for streaming continuous video and audio mixes across mobile and web. Please let me know if you notice any bug or error. Your support is tremendously appreciated.",
            NEWS_2026_TITLE.name to "New 2026 design",
            OUTPUT_TITLE.name to "Audio outputs",
            PHOTO_LOGO.name to "%s logo",
            PHOTO_VENUE_1_SUBTITLE.name to "Debauchery!",
            PHOTO_VENUE_2_SUBTITLE.name to "VJset plus live singer",
            PHOTO_VENUE_EVENTS.name to "Fridays, Saturdays",
            PLAYER_PAUSE.name to "Pause",
            PLAYER_PLAY.name to "Play",
            PLAYER_UNAVAILABLE.name to "This video cannot be played.",
            ROUTE_ABOUT.name to "About",
            ROUTE_CHANNELS.name to "Chans",
            ROUTE_CONTACT.name to "Contact",
            ROUTE_EDITS.name to "Edits",
            ROUTE_MIXES.name to "Mixes",
            ROUTE_NEWS.name to "News",
            ROUTE_PHOTOS.name to "Photos",
            ROUTE_TRACKS.name to "Tracks",
            SHARE_COPIED.name to "Link copied to clipboard!",
            SHARE_MESSAGE.name to "Copy and share the website link:",
            SHARE_TITLE.name to "Share Tremenz",
            STATUS_EMPTY.name to "Nothing here yet",
            STATUS_FAILED.name to "Could not load. Check your connection and try again.",
            STATUS_LOADING.name to "Loading",
            TERMS_MESSAGE.name to "You must first acknowledge and agree that these recordings are for private use only.",
            TERMS_TITLE.name to "Terms and Conditions",
            THEME_DAY.name to "Switch to day theme",
            THEME_NIGHT.name to "Switch to night theme"
        )
    }
}

